
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import { environment } from "../../environments/environment";
import { getPerformanceApiBase } from "../service/modulo-desempeno/mock/mock-config";

let echoInstance: Echo<any> | null = null;
window.Pusher = Pusher;

// console.log("log laravel-echo.ts!!")

// export const echo = new Echo({
//   authEndpoint: environment.MIX_PUSHER_BACKEND_API + '/broadcasting/auth',
//   broadcaster: 'pusher',
//   key: environment.MIX_PUSHER_APP_KEY,
//   wsHost: environment.MIX_PUSHER_HOST,
//   wsPort: environment.MIX_PUSHER_PORT,
//   wssPort: environment.MIX_PUSHER_PORT,
//   forceTLS: environment.MIX_PUSHER_TLS,
//   encrypted: true,
//   disableStats: true,
//   enabledTransports: ['ws', 'wss'],
//   cluster: environment.MIX_PUSHER_APP_CLUSTER,
// });

// Los canales de Desempeño los autoriza el backend de Desempeño, con su propio token; el
// resto, el backend de aprendizaje con el token de sesión. El socket es uno solo: los dos
// backends comparten la app de Pusher/soketi, así que la firma de cualquiera de los dos vale
// para la misma conexión.
//
// `presence-performance.` es el canal de presencia del workspace (`performance.web.<slug>`).
// `private-performance.process.` es el canal del estado de UN proceso (C2, design.md §4.11,
// `PerformanceRealtimeService.watchProcess()`), autorizado igual: mismo backend, mismo token.
// Una lista, no un solo prefijo, para que sumar un canal nuevo de Desempeño no toque la lógica
// de abajo (el plan B de la tarea 0, design.md §6.2, agrega el suyo acá).
const PERFORMANCE_CHANNEL_PREFIXES = ['presence-performance.', 'private-performance.process.'];

type ChannelAuthCallback = (error: Error | null, data: any) => void;

export const resolveChannelAuth = (channelName: string, pusherApiUrl: string, token: string) => {
  if (PERFORMANCE_CHANNEL_PREFIXES.some((prefix) => channelName.startsWith(prefix))) {
    return {
      endpoint: getPerformanceApiBase() + 'broadcasting/auth',
      batchEndpoint: getPerformanceApiBase() + 'broadcasting/auth/batch',
      // Se lee en cada autorización: el token de Desempeño se obtiene recién al entrar al
      // módulo, después de creada esta instancia.
      token: localStorage.getItem('performance_token'),
    };
  }

  return { endpoint: pusherApiUrl + 'broadcasting/auth', batchEndpoint: null, token };
};

const authorizeChannel = (
  endpoint: string,
  token: string,
  socketId: string,
  channelName: string,
  callback: ChannelAuthCallback,
) => {
  fetch(endpoint, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ socket_id: socketId, channel_name: channelName }),
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`No se pudo autorizar ${channelName} (${response.status})`);
      }
      return response.json();
    })
    .then((data) => callback(null, data))
    .catch((error) => callback(error, null));
};

// Pusher pide una autorización por canal, y la app se suscribe al canal de CADA proceso activo
// de la persona (`watchActiveProcesses()`): al entrar a Desempeño salían 20 o más POST en
// paralelo. Las suscripciones de un mismo momento llegan acá dentro de unos milisegundos (el
// bucle de procesos, o la reconexión, que re-suscribe todos los canales de una vez), así que se
// juntan durante `delayMs` y viajan en un solo POST a `broadcasting/auth/batch`.
//
// Si ese endpoint no existe todavía (404: backend sin desplegar) o el lote falla entero, cada
// canal se autoriza por separado como antes: la app nunca queda sin canales por el lote.
export const createChannelAuthBatcher = (fetchFn: typeof fetch = (input, init) => fetch(input, init), delayMs = 20) => {
  let pending = new Map<string, { channels: Map<string, ChannelAuthCallback[]> }>();
  let timer: ReturnType<typeof setTimeout> | null = null;

  const fallback = (endpoint: string, token: string, socketId: string, channels: Map<string, ChannelAuthCallback[]>) => {
    for (const [channelName, callbacks] of channels) {
      authorizeChannel(endpoint, token, socketId, channelName, (error, data) => {
        callbacks.forEach((callback) => callback(error, data));
      });
    }
  };

  const send = (socketId: string, channels: Map<string, ChannelAuthCallback[]>) => {
    // Todos los canales del lote son de Desempeño: cualquiera da el mismo backend y token.
    const [firstChannel] = channels.keys();
    const auth = resolveChannelAuth(firstChannel, '', '');

    if (!auth.token) {
      const error = new Error('Sin token para autorizar los canales de Desempeño');
      channels.forEach((callbacks) => callbacks.forEach((callback) => callback(error, null)));
      return;
    }

    fetchFn(auth.batchEndpoint, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${auth.token}`,
      },
      body: JSON.stringify({ socket_id: socketId, channel_names: [...channels.keys()] }),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Lote de autorización rechazado (${response.status})`);
        return response.json();
      })
      // El rechazo va en el segundo argumento y no en un `catch`: un error dentro de los
      // callbacks de Pusher no tiene que disparar un segundo intento canal por canal.
      .then(
        (data) => {
          for (const [channelName, callbacks] of channels) {
            const result = data?.channels?.[channelName];
            const error = result?.auth
              ? null
              : new Error(`No se pudo autorizar ${channelName} (${result?.status ?? 'sin respuesta'})`);
            callbacks.forEach((callback) => callback(error, error ? null : result));
          }
        },
        () => fallback(auth.endpoint, auth.token, socketId, channels),
      );
  };

  const flush = () => {
    const batches = pending;
    pending = new Map();
    timer = null;
    batches.forEach((batch, socketId) => send(socketId, batch.channels));
  };

  return {
    authorize(socketId: string, channelName: string, callback: ChannelAuthCallback) {
      const batch = pending.get(socketId) ?? { channels: new Map<string, ChannelAuthCallback[]>() };
      batch.channels.set(channelName, [...(batch.channels.get(channelName) ?? []), callback]);
      pending.set(socketId, batch);
      timer ??= setTimeout(flush, delayMs);
    },
  };
};

const performanceAuthBatcher = createChannelAuthBatcher();

export const initializeEcho = (token: string, MIX_PUSHER_APP_KEY, MIX_PUSHER_HOST, pusherApiUrl) => {
  if (!echoInstance) {
    echoInstance = new Echo({
      broadcaster: 'pusher',
      key: MIX_PUSHER_APP_KEY,
      wsHost: MIX_PUSHER_HOST,
      wsPort: environment.MIX_PUSHER_PORT,
      wssPort: environment.MIX_PUSHER_PORT,
      forceTLS: environment.MIX_PUSHER_TLS,
      encrypted: true,
      disableStats: true,
      enabledTransports: ['ws', 'wss'],
      cluster: environment.MIX_PUSHER_APP_CLUSTER,
      // Con `channelAuthorization`, pusher-js ignora `authEndpoint`/`auth`: toda autorización
      // de canal pasa por este handler, que elige backend y token según el canal.
      channelAuthorization: {
        customHandler: ({ socketId, channelName }, callback) => {
          const auth = resolveChannelAuth(channelName, pusherApiUrl, token);

          if (!auth.token) {
            callback(new Error('Sin token para autorizar ' + channelName), null);
            return;
          }

          if (auth.batchEndpoint) {
            performanceAuthBatcher.authorize(socketId, channelName, callback);
            return;
          }

          authorizeChannel(auth.endpoint, auth.token, socketId, channelName, callback);
        },
      },
    });
  }
  return echoInstance;
};

export const getEcho = () => {
  if (!echoInstance) {
    console.log('Echo no ha sido inicializado.')
  }
  return echoInstance;
};

// Desconecta y descarta la instancia. Llamarlo SIEMPRE en lugar de `getEcho().disconnect()`:
// sin soltar la referencia, initializeEcho() devolvería para siempre la instancia ya
// desconectada —y con el token anterior en auth.headers—, así que al volver a iniciar
// sesión sin recargar la app ningún canal conectaría y /broadcasting/auth respondería 403.
export const destroyEcho = () => {
  if (!echoInstance) {
    return;
  }

  try {
    echoInstance.disconnect();
  } catch (error) {
    console.error('Error al desconectar Echo.', error);
  }

  echoInstance = null;
};





















// const echo = new Echo({
//     authEndpoint: environment.VITE_BACKEND_API + '/broadcasting/auth',
//     broadcaster: 'pusher',
//     key: environment.MIX_PUSHER_APP_KEY,
//     wsHost: environment.MIX_PUSHER_HOST,
//     wsPort: environment.MIX_PUSHER_PORT,
//     wssPort: environment.MIX_PUSHER_PORT,
//     forceTLS: environment.MIX_PUSHER_TLS,
//     encrypted: true,
//     disableStats: true,
//     enabledTransports: ['ws', 'wss'],
//     cluster: environment.MIX_PUSHER_APP_CLUSTER,

//     auth: {
//         headers: {
//             Authorization: 'Bearer ' + token,
//             // 'Content-Type': 'application/json',
//         }
//     }
// });

// export { echo }
